import React, { forwardRef, useMemo } from "react";
import { Grid } from "components/Grid";
import { useGridDetailsContextMenu } from "components/Grid/hooks/useGridDetailsContextMenu";
import { useTranslation } from "react-i18next";
import { netCall } from "utils/netCall";
import { ManagementApiUrl } from "environment/routeSettings";
import Spinner from "components/Spinner";
import CustomStore from "devextreme/data/custom_store";
import { generateFiltersByAcceptingCustomStringInput } from "components/Grid/utils";
import { DataGridRef } from "devextreme-react/data-grid";
import { SortObjectByPropName } from "utils/arrayTools";
import { Enum2Array, EnumFlag2Array } from "utils/enumTools";
import {
  WaterMeterActivityStatus,
  WaterMeterAlarmsType,
  WaterMeterLocationType,
  WaterMeterModelType,
  WaterMeterType,
} from "types/dto";
import { TWaterMetersGridRow } from "components/Grid/grids/WaterMetersGrid/types";
import { ActivityStatusBadge } from "components/Grid/grids/WaterMetersGrid/ActivityStatusBadge";
import { useRegion_v2 } from "contexts/regions_v2";
import { GridTypes } from "components/Grid/grids/constants";
import { format } from "date-fns-jalali";
import { getHourDiff, getJalaliDate } from "utils/dateTimeTools";

type TProps = {
  provinceId?: string;
  cityId?: string;
  jurisdictionId?: string;
  actionButtons?: {
    icon: string;
    hint: string;
    onClick: (row: any) => void;
  }[];
  isLoading?: boolean;
  onRowClick?: (e: TWaterMetersGridRow) => void;
  /**
   * Adds right-click -> "view details" / "open in new tab" on each row.
   * Opt-in rather than default: this grid also renders inside chooser
   * modals, where navigating away would discard the user's selection.
   */
  enableDetailsContextMenu?: boolean;
};
const WaterMetersGrid = forwardRef<DataGridRef, TProps>((props, ref) => {
  const {
    jurisdictionId,
    provinceId,
    cityId,
    isLoading,
    actionButtons,
    onRowClick,
    enableDetailsContextMenu,
  } = props;

  // Called unconditionally (rules of hooks); the prop only decides whether
  // the resulting menu is handed to the grid.
  const detailsContextMenu = useGridDetailsContextMenu((row) =>
    row?.id ? `/dashboard/meters/details/${row.id}` : undefined
  );
  const { t } = useTranslation();
  const { selectedRegion, allJurisdictions, allCities, allProvinces } =
    useRegion_v2();

  const dataSource = useMemo(() => {
    return new CustomStore({
      key: "id",
      load: async (loadOption) => {
        if (loadOption.filter) {
          loadOption.filter = generateFiltersByAcceptingCustomStringInput(
            loadOption.filter
          );
        }
        const res = await netCall({
          method: "POST",
          endPoint: `${ManagementApiUrl()}/api/WaterMeter/GetWaterMeters`,
          body: {
            loadOptions: { ...loadOption, requireTotalCount: false },
            provinceId: provinceId || selectedRegion.province?.id,
            cityId: cityId || selectedRegion.city?.id,
            jurisdictionId: jurisdictionId || selectedRegion.jurisdiction?.id,
          },
        });
        return res.results;
      },
    });
  }, [provinceId, cityId, jurisdictionId]);

  const columns = useMemo(() => {
    // Individual alarm flags (excluding None=0) for the bitwise header filter.
    const alarmFlagItems = Object.keys(WaterMeterAlarmsType)
      .filter((k) => isNaN(Number(k)))
      .map((k) => ({
        key: k,
        value: (WaterMeterAlarmsType as any)[k] as number,
      }))
      .filter((x) => x.value !== 0)
      .map((x) => ({ text: t("enum-" + x.key), value: x.value }));

    const _columns: any[] = [
      {
        dataField: "activityStatus",
        caption: t("connectionStatus"),
        cellRender: (cell: any) => <ActivityStatusBadge value={cell.value} />,
        lookup: {
          dataSource: SortObjectByPropName(
            Enum2Array(WaterMeterActivityStatus),
            "name"
          ),
          valueExpr: "value",
          displayExpr: "name",
        },
      },
      {
        dataField: "serialNo",
        caption: t("serialNo"),
      },
      {
        dataField: "lastReading",
        caption: t("lastReading"),
      },
      {
        dataField: "lastAlarm",
        caption: t("lastAlarm"),
        allowFiltering: false,
        allowHeaderFiltering: true,
        // Header-filter selections are intercepted in load() → "has ANY" bitmask;
        // default equality (["lastAlarm","=",bit]) is what the extractor parses.
        headerFilter: { dataSource: alarmFlagItems, allowSearch: true },
        calculateCellValue: (data: any) => {
          const values = EnumFlag2Array(WaterMeterAlarmsType, data.lastAlarm);
          const res = [];
          if (values) {
            for (let i = 0; i < values.length; i++) {
              res.push(t("enum-" + WaterMeterAlarmsType[values[i]]));
            }
          }
          return res.join(", ");
        },
        hasFlag: true,
      },
      {
        dataField: "lastSyncDT",
        caption: t("lastSyncDT"),
        dataType: "dateTime",
        calculateCellValue: (data: any) => {
          return getJalaliDate({
            date: data?.lastSyncDT,
          });
        },
      },

      {
        dataField: "isInstalled",
        caption: t("isInstalled"),
        dataType: "boolean",
      },
      {
        dataField: "locationType",
        caption: t("waterMeterLocationType"),
        lookup: {
          dataSource: SortObjectByPropName(
            Enum2Array(WaterMeterLocationType),
            "name"
          ),
          valueExpr: "value",
          displayExpr: "name",
        },
      },
      {
        caption: t("installationInfo"),
        columns: [
          {
            dataField: "installation.subscriberName",
            caption: t("subscriberName"),
          },
          {
            dataField: "installation.identifierId",
            caption: t("identifierId"),
          },
          {
            dataField: "installation.address",
            caption: t("address"),
            visible: false,
          },
          {
            dataField: "installation.installationDate",
            caption: t("installationDate"),
            dataType: "date",
            calculateCellValue: (data: any) => {
              return getJalaliDate({
                date: data?.installation?.installationDate,
              });
            },
          },
        ],
      },
      {
        dataField: "modelType",
        caption: t("waterMeterModelType"),
        lookup: {
          dataSource: SortObjectByPropName(
            Enum2Array(WaterMeterModelType),
            "name"
          ),
          valueExpr: "value",
          displayExpr: "name",
        },
      },
      {
        dataField: "waterMeterType",
        caption: t("waterMeterType"),
        visible: false,
        lookup: {
          dataSource: SortObjectByPropName(Enum2Array(WaterMeterType), "name"),
          valueExpr: "value",
          displayExpr: "name",
        },
      },
      {
        dataField: "provinceId",
        caption: t("province"),
        visible: false,
        lookup: {
          dataSource: allProvinces,
          valueExpr: "id",
          displayExpr: "name",
        },
      },
      {
        dataField: "cityId",
        caption: t("city"),
        visible: false,
        lookup: {
          dataSource: allCities,
          valueExpr: "id",
          displayExpr: "name",
        },
      },
      {
        dataField: "jurisdictionId",
        caption: t("jurisdiction"),
        visible: false,
        lookup: {
          dataSource: allJurisdictions,
          valueExpr: "id",
          displayExpr: "name",
        },
      },
      {
        visible: false,
        dataField: "id",
        caption: t("id"),
      },
    ];
    if (actionButtons && actionButtons.length > 0) {
      _columns.push({
        caption: t("actions"),
        visible: true,
        width: 110,
        buttons: actionButtons.map((button) => ({
          icon: button.icon,
          hint: button.hint,
          onClick: (e: any) => button.onClick(e.row.data),
        })),
      });
    }
    return _columns;
  }, [t, actionButtons]);

  return (
    <>
      <div className={"w-full flex gap-x flex-1 min-h-0"}>
        {isLoading && (
          <div className="z-[200] absolute bg-mofidGray-40 backdrop-blur-[2px] bg-opacity-30 top-0 left-0 bottom-0 right-0 flex justify-center items-center rounded-md">
            <p>{t("gettingInformation")}</p>
            <Spinner />
          </div>
        )}

        <Grid
          ref={ref}
          gridKey={"id"}
          dataSource={dataSource}
          storageKey={GridTypes.WaterMeters}
          columns={columns}
          rowContextMenu={
            enableDetailsContextMenu ? detailsContextMenu : undefined
          }
          hasSearchPanel={false}
          hasPagination={true}
          hasGroupingFeatures={false}
          hasFilterBuilder={true}
          onRowClick={(e) => {
            e?.data && onRowClick?.(e.data as TWaterMetersGridRow);
          }}
          height={"100%"}
        />
      </div>
    </>
  );
});

export default WaterMetersGrid;
